import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { type UserSettingsAdmin } from '../types';
import { zodUserSettingsAdminSchema } from '../validations';

/**
 * Fetches the settings stored on a user. Requires VESTIGAS admin access.
 *
 * @param userId - The ID of the user whose settings to fetch.
 * @param options - Optional Ky request options including signal for aborting the request.
 * @returns The user's stored settings, reduced to the user feature flags.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_settings_for_user_vadmin_user__user_id__settings_get
 */
export const fetchUserSettingsAdmin = async (
  userId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.USER.SETTINGS_ADMIN.GET(userId), {
        ...options,
        validate: {
          ...options?.validate,
          response: zodUserSettingsAdminSchema,
        },
      })
      .json<UserSettingsAdmin>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    log.error({
      message: 'Error fetching user settings',
      error,
    });

    throw error;
  }
};
